/**
 * Minimal fetch utilities for replacing axios.
 * ponytail: no interceptors, no instance config — just typed JSON fetching with retry.
 */

export class HttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`HTTP ${status}: ${body}`);
  }
}

export async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, options);
  if (!res.ok) throw new HttpError(res.status, await res.text());
  return res.json();
}

export async function fetchWithRetry<T>(
  url: string,
  options?: RequestInit & { retries?: number; retryDelay?: number }
): Promise<T> {
  const { retries = 3, retryDelay = 1000, ...fetchOpts } = options ?? {};
  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchJSON<T>(url, fetchOpts);
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      // Don't retry on 4xx (client errors) except 429 (rate limited)
      if (err instanceof HttpError && err.status >= 400 && err.status < 500 && err.status !== 429) throw err;
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, attempt)));
      }
    }
  }
  throw lastError;
}
